import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getMethod, postMethod, patchMethod } from "../../services/ApiCallMethods";

export const fetchRequestTypes = createAsyncThunk(
  "changeRequests/fetchTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getMethod(`/api/change-requests/types`);
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch request types");
    }
  }
);

export const fetchChangeRequests = createAsyncThunk(
  "changeRequests/fetchAll",
  async ({ clientId, search, currentPage, itemsPerPage }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      params.append("page", currentPage || 1);
      params.append("limit", itemsPerPage || 10);
      const response = await getMethod(
        `/api/change-requests/${clientId}?${params.toString()}`
      );
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch change requests");
    }
  }
);

export const fetchAdminChangeRequests = createAsyncThunk(
  "changeRequests/fetchAdmin",
  async ({ search, currentPage, itemsPerPage }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      params.append("page", currentPage || 1);
      params.append("limit", itemsPerPage || 10);
      const response = await getMethod(`/api/change-requests?${params.toString()}`);
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch change requests");
    }
  }
);

export const updateRequestStatus = createAsyncThunk(
  "changeRequests/updateStatus",
  async ({ id, status, rejection_comments }, { rejectWithValue }) => {
    try {
      const response = await patchMethod(`/api/change-requests/${id}/status`, { status, rejection_comments });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return { id, status };
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to update status");
    }
  }
);

export const submitChangeRequest = createAsyncThunk(
  "changeRequests/submit",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postMethod(`/api/change-requests`, payload);
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data);
      }
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to submit request");
    }
  }
);

const changeRequestSlice = createSlice({
  name: "changeRequests",
  initialState: {
    requestTypes: [],
    requests: [],
    loading: false,
    submitLoading: false,
    updateLoading: false,
    error: null,
    pagination: {
      currentPage: 1,
      itemsPerPage: 10,
      totalPages: 1,
      totalItems: 0,
    },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRequestTypes.fulfilled, (state, action) => {
        state.requestTypes = action.payload?.data || [];
      })
      .addCase(fetchChangeRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChangeRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload?.data || [];
        const p = action.payload?.pagination;
        if (p) {
          state.pagination = {
            currentPage: p.currentPage,
            itemsPerPage: p.itemsPerPage,
            totalPages: p.totalPages,
            totalItems: p.totalItems,
          };
        }
      })
      .addCase(fetchChangeRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAdminChangeRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminChangeRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload?.data || [];
        const p = action.payload?.pagination;
        if (p) {
          state.pagination = {
            currentPage: p.currentPage,
            itemsPerPage: p.itemsPerPage,
            totalPages: p.totalPages,
            totalItems: p.totalItems,
          };
        }
      })
      .addCase(fetchAdminChangeRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateRequestStatus.pending, (state) => {
        state.updateLoading = true;
      })
      .addCase(updateRequestStatus.fulfilled, (state, action) => {
        state.updateLoading = false;
        const { id, status } = action.payload;
        const req = state.requests.find((r) => r.id === id);
        if (req) {
          req.status = status;
          req.status_label = status.charAt(0).toUpperCase() + status.slice(1);
        }
      })
      .addCase(updateRequestStatus.rejected, (state) => {
        state.updateLoading = false;
      })
      .addCase(submitChangeRequest.pending, (state) => {
        state.submitLoading = true;
      })
      .addCase(submitChangeRequest.fulfilled, (state) => {
        state.submitLoading = false;
      })
      .addCase(submitChangeRequest.rejected, (state) => {
        state.submitLoading = false;
      });
  },
});

export default changeRequestSlice.reducer;
